import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    LinearProgress,
    Alert,
    AlertTitle,
    Chip,
    Divider,
    Grid,
    Paper,
    List,
    ListItem,
    ListItemText,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Collapse,
    IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const UploadArea = styled(Paper)(({ theme, isDragOver }) => ({
    padding: theme.spacing(4),
    textAlign: 'center',
    border: `2px dashed ${isDragOver ? theme.palette.primary.main : theme.palette.grey[300]}`,
    backgroundColor: isDragOver ? theme.palette.action.hover : theme.palette.background.paper,
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    '&:hover': {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.action.hover,
    },
}));

function getCookie(name) {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            if (cookie.substring(0, name.length + 1) === (name + '=')) {
                cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                break;
            }
        }
    }
    return cookieValue;
}

function StudentUpload() {
    const [file, setFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const [activeStep, setActiveStep] = useState(0);
    const [isDragOver, setIsDragOver] = useState(false);
    const [showDetails, setShowDetails] = useState(false);

    const steps = [
        {
            label: 'Select File',
            description: 'Choose an Excel file (.xlsx) to upload',
            icon: '📁'
        },
        {
            label: 'Upload & Process',
            description: 'Upload and process student data',
            icon: '⬆️'
        },
        {
            label: 'Review Results',
            description: 'Review upload results and any errors',
            icon: '📋'
        }
    ];

    const handleFileSelect = (selectedFile) => {
        if (selectedFile && selectedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
            setFile(selectedFile);
            setActiveStep(1);
            setUploadResult(null);
        } else {
            setUploadResult({
                success: false,
                message: 'Please select a valid Excel (.xlsx) file'
            });
        }
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        handleFileSelect(selectedFile);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const droppedFile = e.dataTransfer.files[0];
        handleFileSelect(droppedFile);
    };

    const handleSubmit = async () => {
        if (!file) {
            setUploadResult({
                success: false,
                message: 'Please select a file to upload.'
            });
            return;
        }

        setIsUploading(true);
        setUploadResult(null);

        try {
            const formData = new FormData();
            formData.append('file', file);

            const csrfToken = getCookie('csrftoken');

            const response = await axios.post('/students/api/upload-students/', formData, {
                headers: {
                    'X-CSRFToken': csrfToken,
                    'Content-Type': 'multipart/form-data',
                },
            });

            setUploadResult({
                success: true,
                message: response.data.success,
                data: response.data
            });
            setActiveStep(2);

        } catch (error) {
            console.error('Error uploading file:', error);
            const errorMessage = error.response?.data?.error || 'An unexpected error occurred during upload.';
            setUploadResult({
                success: false,
                message: errorMessage,
                data: error.response?.data
            });
            setActiveStep(2);
        } finally {
            setIsUploading(false);
        }
    };

    const resetUpload = () => {
        setFile(null);
        setUploadResult(null);
        setActiveStep(0);
        setShowDetails(false);
    };

    const formatBytes = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
            <Card elevation={3}>
                <CardContent>
                    <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span style={{ fontSize: '32px' }}>☁️</span>
                        Student Data Upload
                    </Typography>
                    
                    <Typography variant="body1" color="text.secondary" paragraph>
                        Upload student data from Excel files. The system automatically detects both Infinite Campus format and standard format.
                    </Typography>

                    <Divider sx={{ my: 3 }} />

                    {/* Progress Stepper */}
                    <Stepper activeStep={activeStep} orientation="vertical" sx={{ mb: 3 }}>
                        {steps.map((step, index) => (
                            <Step key={step.label}>
                                <StepLabel icon={step.icon}>
                                    <Typography variant="h6">{step.label}</Typography>
                                </StepLabel>
                                <StepContent>
                                    <Typography color="text.secondary">{step.description}</Typography>
                                </StepContent>
                            </Step>
                        ))}
                    </Stepper>

                    {/* File Upload Area */}
                    {activeStep === 0 && (
                        <UploadArea
                            isDragOver={isDragOver}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => document.getElementById('file-upload').click()}
                        >
                            <span style={{ fontSize: '48px', marginBottom: '16px', display: 'block' }}>☁️</span>
                            <Typography variant="h6" gutterBottom>
                                Drop your Excel file here or click to browse
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Supports .xlsx files with student data
                            </Typography>
                            <Button
                                component="label"
                                variant="contained"
                                sx={{ mt: 2 }}
                            >
                                📁 Choose File
                                <VisuallyHiddenInput
                                    id="file-upload"
                                    type="file"
                                    accept=".xlsx"
                                    onChange={handleFileChange}
                                />
                            </Button>
                        </UploadArea>
                    )}

                    {/* File Selected */}
                    {file && activeStep === 1 && (
                        <Card variant="outlined" sx={{ mb: 3 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <span style={{ color: 'green' }}>✅</span>
                                    File Selected
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="text.secondary">Filename:</Typography>
                                        <Typography variant="body1">{file.name}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="text.secondary">Size:</Typography>
                                        <Typography variant="body1">{formatBytes(file.size)}</Typography>
                                    </Grid>
                                </Grid>
                                
                                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                                    <Button
                                        variant="contained"
                                        onClick={handleSubmit}
                                        disabled={isUploading}
                                        size="large"
                                    >
                                        {isUploading ? '⏳ Processing...' : '⬆️ Upload & Process'}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={resetUpload}
                                        disabled={isUploading}
                                    >
                                        Choose Different File
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                    {/* Upload Progress */}
                    {isUploading && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" gutterBottom>
                                Processing file...
                            </Typography>
                            <LinearProgress />
                        </Box>
                    )}

                    {/* Upload Results */}
                    {uploadResult && activeStep === 2 && (
                        <Card variant="outlined" sx={{ mb: 3 }}>
                            <CardContent>
                                <Alert 
                                    severity={uploadResult.success ? 'success' : 'error'}
                                    sx={{ mb: 2 }}
                                >
                                    <AlertTitle>
                                        {uploadResult.success ? 'Upload Successful!' : 'Upload Failed'}
                                    </AlertTitle>
                                    {uploadResult.message}
                                </Alert>

                                {uploadResult.success && uploadResult.data && (
                                    <Box>
                                        <Grid container spacing={2} sx={{ mb: 2 }}>
                                            <Grid item xs={12} sm={4}>
                                                <Chip 
                                                    label={`${uploadResult.data.total_processed || 0} Students Processed`}
                                                    color="success"
                                                    variant="outlined"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <Chip 
                                                    label={`Format: ${uploadResult.data.format_detected || 'Unknown'}`}
                                                    color="info"
                                                    variant="outlined"
                                                />
                                            </Grid>
                                            {uploadResult.data.error_count > 0 && (
                                                <Grid item xs={12} sm={4}>
                                                    <Chip 
                                                        label={`${uploadResult.data.error_count} Errors`}
                                                        color="warning"
                                                        variant="outlined"
                                                    />
                                                </Grid>
                                            )}
                                        </Grid>

                                        {uploadResult.data.errors && uploadResult.data.errors.length > 0 && (
                                            <Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                    <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                                                        Error Details ({uploadResult.data.errors.length})
                                                    </Typography>
                                                    <IconButton
                                                        onClick={() => setShowDetails(!showDetails)}
                                                        size="small"
                                                    >
                                                        {showDetails ? '▲' : '▼'}
                                                    </IconButton>
                                                </Box>
                                                <Collapse in={showDetails}>
                                                    <List dense>
                                                        {uploadResult.data.errors.slice(0, 10).map((error, index) => (
                                                            <ListItem key={index}>
                                                                <ListItemText 
                                                                    primary={`❌ ${error}`}
                                                                    primaryTypographyProps={{ variant: 'body2' }}
                                                                />
                                                            </ListItem>
                                                        ))}
                                                        {uploadResult.data.errors.length > 10 && (
                                                            <ListItem>
                                                                <ListItemText 
                                                                    primary={`ℹ️ ... and ${uploadResult.data.errors.length - 10} more errors`}
                                                                    primaryTypographyProps={{ variant: 'body2', fontStyle: 'italic' }}
                                                                />
                                                            </ListItem>
                                                        )}
                                                    </List>
                                                </Collapse>
                                            </Box>
                                        )}
                                    </Box>
                                )}

                                <Box sx={{ mt: 3 }}>
                                    <Button
                                        variant="contained"
                                        onClick={resetUpload}
                                    >
                                        ☁️ Upload Another File
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                    {/* Format Information */}
                    <Card variant="outlined" sx={{ mt: 3, bgcolor: 'background.default' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span style={{ color: '#1976d2' }}>ℹ️</span>
                                Supported Formats
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" gutterBottom>Infinite Campus Format:</Typography>
                                    <Typography variant="body2" component="div">
                                        • student_firstName<br/>
                                        • student_lastName<br/>
                                        • pcontact_email<br/>
                                        • student_grade<br/>
                                        • student_activeToday
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" gutterBottom>Standard Format:</Typography>
                                    <Typography variant="body2" component="div">
                                        • first_name<br/>
                                        • last_name<br/>
                                        • email<br/>
                                        • grade_level<br/>
                                        • is_active
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </CardContent>
            </Card>
        </Box>
    );
}

export default StudentUpload;
